import { useQuery } from '@tanstack/react-query';
import { ENTRY_SOURCES, looseFrom, rel, type Row } from './entry-sources';

/**
 * "I sent something — where did it go?"
 *
 * A site engineer submits an entry and it vanishes: it is pending, so it counts
 * towards nothing and appears in no ledger they can see. Without this screen the
 * only feedback is a toast that disappears in three seconds, which is not enough
 * to trust a system with.
 *
 * Shares ENTRY_SOURCES with the admin's Approvals inbox, so the two screens can
 * never describe the same entry differently.
 */

export type SubmissionStatus = 'pending' | 'approved' | 'rejected';

export interface SubmissionRow {
  id: string;
  table: string;
  kind: string;
  date: string;
  summary: string;
  projectName: string;
  status: SubmissionStatus;
  submittedAt: string;
  reviewedAt: string | null;
  reviewNote: string | null;
}

export function useMySubmissions(userId: string | undefined, status: string) {
  return useQuery({
    queryKey: ['my-submissions', userId ?? '', status],
    enabled: !!userId,
    queryFn: async (): Promise<SubmissionRow[]> => {
      const results = await Promise.all(
        ENTRY_SOURCES.map(async (src) => {
          let q = looseFrom(src.table)
            .select(src.select)
            .eq('submitted_by', userId!)
            .is('deleted_at', null)
            .order(src.dateCol, { ascending: false })
            .limit(50);
          if (status) q = q.eq('approval_status', status);
          // A bill line is not a separate submission — the bill stands for it.
          if (src.filter) q = src.filter(q);

          const { data, error } = await q;
          // A table this user has no read path to returns an error. That is a
          // permission boundary working, not a failure — skip it rather than
          // breaking the whole page.
          if (error) return [];

          return (data ?? []).map((raw): SubmissionRow => {
            const r = raw as Row;
            return {
              id: String(r.id),
              table: src.table,
              kind: src.kind,
              date: String(r[src.dateCol] ?? ''),
              summary: src.describe(r),
              projectName: rel(r.projects),
              status: r.approval_status as SubmissionStatus,
              submittedAt: String(r.submitted_at ?? ''),
              reviewedAt: (r.reviewed_at as string) ?? null,
              reviewNote: (r.review_note as string) ?? null,
            };
          });
        }),
      );

      return results
        .flat()
        .sort((a, b) => (a.submittedAt < b.submittedAt ? 1 : a.submittedAt > b.submittedAt ? -1 : 0));
    },
  });
}
